<script setup lang="ts">
import { NuxtLinkLocale } from '#components';

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();

const emit = defineEmits<{
  (e: 'click', value: string): void;
}>();

interface PropTypes {
  label: string;
  value?: string;
  size?: 'md' | 'lg';
  variant?: 'button' | 'text';
  disabled?: boolean;
  selected?: boolean;
  inverted?: boolean;
  element?: 'anchor' | 'button';
  targetBlank?: boolean;
  url?: string;
}

const props = withDefaults(defineProps<PropTypes>(), {
  value: undefined,
  variant: 'button',
  element: 'button',
  url: '',
  size: 'md',
});

const isAnchor = computed(() => props.element === 'anchor');

const componentAttrs = computed(() => {
  return isAnchor.value
    ? {
        to: props.url,
        target: props.targetBlank ? '_blank' : null,
        ...attrs,
        class: '',
      }
    : { type: 'button', disabled: props.disabled, ...attrs };
});

const buttonClasses = computed(() => ({
  'relative flex items-center justify-center truncate rounded-full text-label-14 transition-colors':
    true,

  'bg-grey-50 text-dark-blue hover:bg-grey-100':
    !props.inverted && !props.disabled && !props.selected,
  'bg-dark-blue text-white hover:bg-grey-100':
    !props.inverted && !props.disabled && props.selected,

  'bg-white-10 text-white hover:bg-white-20':
    props.inverted && !props.disabled && !props.selected,
  'bg-white text-dark-blue hover:bg-white-20':
    props.inverted && !props.disabled && props.selected,

  'bg-grey-50 text-dark-blue-20 hover:bg-grey-50':
    !props.inverted && props.disabled,
  'bg-white-10 text-white-20 hover:bg-white-10':
    props.inverted && props.disabled,

  'h-12 p-4': props.size === 'md',
  'h-16 p-5': props.size === 'lg',

  'cursor-pointer': !props.disabled,
  'cursor-default': props.disabled,
}));

const textClasses = computed(() => ({
  'relative flex h-12 items-center justify-center truncate text-label-14 transition-colors':
    true,

  selected: props.selected && !props.inverted,
  'selected-inverted': props.selected && props.inverted,

  'text-dark-blue hover:text-dark-blue-50 focus:text-dark-blue-50':
    !props.disabled && !props.inverted,
  'text-dark-blue-20': props.disabled && !props.inverted,

  'text-white hover:text-white-50 focus-visible:text-white-50':
    !props.disabled && props.inverted,
  'text-white-20': props.disabled && props.inverted,
}));

const classes = computed(() => {
  return props.variant === 'button' ? buttonClasses.value : textClasses.value;
});

const handleClick = () => {
  if (!props.disabled && props.value) emit('click', props.value);
};
</script>

<template>
  <component
    :is="isAnchor ? NuxtLinkLocale : 'button'"
    :class="classes"
    v-bind="componentAttrs"
    @click="handleClick"
  >
    {{ label }}
  </component>
</template>

<style scoped>
.selected,
.selected-inverted {
  &::after {
    content: '';
    @apply absolute bottom-0 left-0 right-0 h-0.5 transition-colors;
  }
}

.selected::after {
  @apply bg-dark-blue;
}
.selected-inverted::after {
  @apply bg-white;
}
</style>
